/**
* @file 增强统计分析
* @description 包含经营概览、城市下单分布、线路分析、乘客人群分析、司机分析、车辆排行、用户分析、时段分析
*/
<template>
  <div class="analysis-statistics">
    <!-- 顶部筛选条件 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-radio-group v-model="timeType" size="small" @change="onTimeTypeChange">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="refreshAll" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 经营概览卡片 -->
    <div class="section-title"><i class="el-icon-data-analysis"></i> 经营概览</div>
    <div class="overview-cards">
      <div class="ov-card" v-for="item in overviewCards" :key="item.label">
        <template v-if="item.isRate">
          <el-progress type="circle" :width="52" :stroke-width="5" :percentage="Number(item.value) || 0" :color="item.rateColor" />
          <div class="ov-card-info">
            <div class="ov-card-label">{{ item.label }}</div>
          </div>
        </template>
        <template v-else>
          <div class="ov-card-icon" :style="{ background: item.color }">
            <i :class="item.icon"></i>
          </div>
          <div class="ov-card-info">
            <div class="ov-card-value">{{ item.value }}</div>
            <div class="ov-card-label">{{ item.label }}</div>
          </div>
        </template>
      </div>
    </div>

    <!-- 图表区域：3列布局 -->
    <div class="charts-grid">
      <!-- 国家/地区下单分布 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-place"></i> 国家/地区下单分布</span>
        </div>
        <div class="chart-body" ref="countryChart"></div>
      </div>

      <!-- 城市下单分布 TOP10 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-location-outline"></i> 城市下单分布 TOP10</span>
        </div>
        <div class="chart-body" ref="cityChart"></div>
      </div>

      <!-- 乘客性别分布 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-user"></i> 乘客性别分布</span>
        </div>
        <div class="chart-body" ref="genderChart"></div>
      </div>

      <!-- 乘客年龄段分布 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-user"></i> 乘客年龄段分布</span>
        </div>
        <div class="chart-body" ref="ageChart"></div>
      </div>

      <!-- 车票类型分布 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-tickets"></i> 车票类型分布</span>
        </div>
        <div class="chart-body" ref="ticketChart"></div>
      </div>

      <!-- 新老用户趋势 -->
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-user-solid"></i> 新老用户订单趋势</span>
        </div>
        <div class="chart-body" ref="userTrendChart"></div>
      </div>

    </div>

    <!-- 时段分析：2列布局 -->
    <div class="charts-grid-half">
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-time"></i> 下单时段分布（按小时）</span>
        </div>
        <div class="chart-body" ref="hourChart"></div>
      </div>
      <div class="chart-card">
        <div class="chart-card-header">
          <span><i class="el-icon-date"></i> 下单分布（按星期）</span>
        </div>
        <div class="chart-body" ref="weekChart"></div>
      </div>
    </div>

    <!-- 表格区域 -->
    <div class="table-card" style="margin-bottom: 20px;">
      <div class="section-title"><i class="el-icon-s-data"></i> 线路经营分析</div>
      <el-table :data="lineData" border stripe size="small" max-height="400" style="width:100%">
        <el-table-column prop="organizationName" label="机构" min-width="90" show-overflow-tooltip />
        <el-table-column prop="lineName" label="线路" min-width="90" show-overflow-tooltip />
        <el-table-column prop="orderCount" label="订单数" min-width="65" sortable />
        <el-table-column prop="finishCount" label="完成" min-width="55" sortable />
        <el-table-column prop="cancelCount" label="退票" min-width="55" sortable />
        <el-table-column prop="passengerCount" label="乘客数" min-width="65" sortable />
        <el-table-column label="收入(元)" min-width="80" sortable prop="totalRevenue">
          <template slot-scope="{row}">{{ row.totalRevenue || '0.00' }}</template>
        </el-table-column>
        <el-table-column label="客单价" min-width="70" prop="avgOrderFee">
          <template slot-scope="{row}">{{ row.avgOrderFee || '0.00' }}</template>
        </el-table-column>
        <el-table-column label="完成率" min-width="100" prop="completionRate">
          <template slot-scope="{row}">
            <el-progress v-if="row.completionRate != null" :percentage="Number(row.completionRate) || 0" :stroke-width="14" :text-inside="true" :color="row.completionRate >= 80 ? '#67C23A' : row.completionRate >= 50 ? '#E6A23C' : '#F56C6C'" />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="新客" min-width="50" prop="newCustomerCount" />
        <el-table-column label="老客" min-width="50" prop="oldCustomerCount" />
        <el-table-column label="本地" min-width="50" prop="localCount" />
        <el-table-column label="异地" min-width="50" prop="remoteCount" />
      </el-table>
    </div>

    <div class="table-card" style="margin-bottom: 20px;">
      <div class="section-title"><i class="el-icon-user"></i> 线路×人群交叉分析</div>
      <el-table :data="linePassengerData" border stripe size="small" max-height="400" style="width:100%">
        <el-table-column prop="organizationName" label="机构" min-width="90" show-overflow-tooltip />
        <el-table-column prop="lineName" label="线路" min-width="90" show-overflow-tooltip />
        <el-table-column prop="totalPassengers" label="总乘客" min-width="60" sortable />
        <el-table-column prop="maleCount" label="男" min-width="45" />
        <el-table-column prop="femaleCount" label="女" min-width="45" />
        <el-table-column prop="childCount" label="儿童" min-width="50" />
        <el-table-column prop="youthCount" label="青少年" min-width="60" />
        <el-table-column prop="adultCount" label="成人" min-width="50" />
        <el-table-column prop="elderlyCount" label="老年" min-width="50" />
        <el-table-column label="老年占比" min-width="100" prop="elderlyRate">
          <template slot-scope="{row}">
            <el-progress v-if="row.elderlyRate != null" :percentage="Number(row.elderlyRate) || 0" :stroke-width="14" :text-inside="true" :color="row.elderlyRate >= 30 ? '#F56C6C' : row.elderlyRate >= 15 ? '#E6A23C' : '#67C23A'" />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="儿童占比" min-width="100" prop="childRate">
          <template slot-scope="{row}">
            <el-progress v-if="row.childRate != null" :percentage="Number(row.childRate) || 0" :stroke-width="14" :text-inside="true" :color="row.childRate >= 30 ? '#F56C6C' : row.childRate >= 15 ? '#E6A23C' : '#67C23A'" />
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="fullTicketCount" label="全票" min-width="50" />
        <el-table-column prop="halfTicketCount" label="半价" min-width="50" />
        <el-table-column prop="freeTicketCount" label="免票" min-width="50" />
      </el-table>
    </div>

    <div class="tables-grid">
      <div class="table-card">
        <div class="section-title"><i class="el-icon-s-custom"></i> 司机经营排行 TOP20</div>
        <el-table :data="driverData" border stripe size="small" max-height="400" style="width:100%">
          <el-table-column type="index" label="序号" width="50" />
          <el-table-column prop="organizationName" label="机构" min-width="80" show-overflow-tooltip />
          <el-table-column prop="driverName" label="司机" min-width="80" />
          <el-table-column prop="driverPhone" label="电话" min-width="110" />
          <el-table-column prop="orderCount" label="工单" min-width="60" sortable />
          <el-table-column prop="finishCount" label="完成" min-width="60" sortable />
          <el-table-column prop="passengerCount" label="乘客" min-width="60" />
          <el-table-column label="收入(元)" min-width="90" sortable prop="totalRevenue">
            <template slot-scope="{row}">{{ row.totalRevenue || '0.00' }}</template>
          </el-table-column>
          <el-table-column label="完成率" min-width="100" prop="completionRate">
            <template slot-scope="{row}">
              <el-progress v-if="row.completionRate != null" :percentage="Number(row.completionRate) || 0" :stroke-width="14" :text-inside="true" :color="row.completionRate >= 80 ? '#67C23A' : row.completionRate >= 50 ? '#E6A23C' : '#F56C6C'" />
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="table-card">
        <div class="section-title"><i class="el-icon-truck"></i> 车辆收入排行 TOP20</div>
        <el-table :data="vehicleData" border stripe size="small" max-height="400" style="width:100%">
          <el-table-column type="index" label="序号" width="50" />
          <el-table-column prop="organizationName" label="机构" min-width="80" show-overflow-tooltip />
          <el-table-column prop="vehiclePlate" label="车辆号牌" min-width="100">
            <template slot-scope="{row}">
              <span v-if="row.vehiclePlate" :style="getPlateStyle(row.vehiclePlate)">{{ formatPlateNumber(row.vehiclePlate) }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="driverName" label="司机" min-width="80" />
          <el-table-column prop="orderCount" label="工单" min-width="60" sortable />
          <el-table-column prop="passengerCount" label="乘客" min-width="60" />
          <el-table-column label="收入(元)" min-width="90" sortable prop="totalRevenue">
            <template slot-scope="{row}">{{ row.totalRevenue || '0.00' }}</template>
          </el-table-column>
          <el-table-column label="客单价" min-width="80" prop="avgOrderFee">
            <template slot-scope="{row}">{{ row.avgOrderFee || '0.00' }}</template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'AnalysisStatistics',
  data() {
    return {
      loading: false,
      timeType: 0,
      overview: {},
      cityData: [],
      lineData: [],
      linePassengerData: [],
      passengerProfile: {},
      driverData: [],
      vehicleData: [],
      userData: [],
      timeSlotData: [],
      // ECharts实例
      charts: {}
    }
  },
  computed: {
    overviewCards() {
      const o = this.overview
      return [
        { label: '总订单', value: o.totalOrders || 0, icon: 'el-icon-document', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '完成订单', value: o.finishOrders || 0, icon: 'el-icon-circle-check', color: 'linear-gradient(135deg,#67C23A,#85CE61)' },
        { label: '退票订单', value: o.cancelOrders || 0, icon: 'el-icon-circle-close', color: 'linear-gradient(135deg,#F56C6C,#F89898)' },
        { label: '总收入(元)', value: o.totalRevenue || '0.00', icon: 'el-icon-coin', color: 'linear-gradient(135deg,#E6A23C,#F0B85A)' },
        { label: '客单价(元)', value: o.avgOrderFee || '0.00', icon: 'el-icon-price-tag', color: 'linear-gradient(135deg,#909399,#B0B3B8)' },
        { label: '总乘客', value: o.totalPassengers || 0, icon: 'el-icon-user', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '新客订单', value: o.newCustomerOrders || 0, icon: 'el-icon-star-on', color: 'linear-gradient(135deg,#67C23A,#85CE61)' },
        { label: '新客占比', value: o.newCustomerRate || 0, icon: 'el-icon-pie-chart', color: 'linear-gradient(135deg,#E6A23C,#F0B85A)', isRate: true, rateColor: '#E6A23C' },
        { label: '本地订单', value: o.localOrders || 0, icon: 'el-icon-location', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '本地占比', value: o.localRate || 0, icon: 'el-icon-map-location', color: 'linear-gradient(135deg,#67C23A,#85CE61)', isRate: true, rateColor: '#67C23A' },
        { label: '国内订单', value: o.domesticOrders || 0, icon: 'el-icon-office-building', color: 'linear-gradient(135deg,#409EFF,#66B1FF)' },
        { label: '海外订单', value: o.overseasOrders || 0, icon: 'el-icon-place', color: 'linear-gradient(135deg,#b37feb,#d3adf7)' },
        { label: '海外占比', value: o.overseasRate || 0, icon: 'el-icon-discover', color: 'linear-gradient(135deg,#b37feb,#d3adf7)', isRate: true, rateColor: '#b37feb' },
        { label: '注册用户', value: o.registeredUsers || 0, icon: 'el-icon-user-solid', color: 'linear-gradient(135deg,#909399,#B0B3B8)' },
        { label: '平台收入(元)', value: o.platformIncome || '0.00', icon: 'el-icon-money', color: 'linear-gradient(135deg,#F56C6C,#F89898)' },
      ]
    }
  },
  mounted() {
    this.refreshAll()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(c => c && c.dispose())
  },
  methods: {
    /** 窗口大小变化时自动调整所有图表尺寸 */
    handleResize() {
      Object.values(this.charts).forEach(c => c && c.resize())
    },
    /** 时间范围切换时重新加载所有数据 */
    onTimeTypeChange() {
      this.refreshAll()
    },
    /** 构造统一查询参数 */
    getParams() {
      return {
        appKey: this.mixinUserInfo ? this.mixinUserInfo.appKey : '',
        timeType: this.timeType
      }
    },
    /** 刷新全部统计数据（并行请求9个接口） */
    async refreshAll() {
      this.loading = true
      const params = this.getParams()
      try {
        await Promise.all([
          this.fetchOverview(params),
          this.fetchCity(params),
          this.fetchLine(params),
          this.fetchPassenger(params),
          this.fetchLinePassenger(params),
          this.fetchDriver(params),
          this.fetchVehicle(params),
          this.fetchUser(params),
          this.fetchTimeSlot(params),
        ])
      } catch (e) {
        console.error('统计数据加载失败', e)
      }
      this.loading = false
      // 所有数据加载完成后，延迟调整所有图表尺寸确保CSS grid布局已稳定
      setTimeout(() => this.handleResize(), 300)
    },

    /** 获取经营概览增强数据 */
    async fetchOverview(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/overview', params)
      if (res && res.code === 1) this.overview = res.data || {}
    },

    /** 获取城市下单分布数据并渲染城市TOP10柱状图和国家分布饼图 */
    async fetchCity(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/city', params)
      this.cityData = (res && res.code === 1 && res.data) ? res.data : []
      this.$nextTick(() => {
        this.renderCountryChart()
        this.renderCityChart()
      })
    },

    /** 获取线路经营分析数据 */
    async fetchLine(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/line', params)
      if (res && res.code === 1) this.lineData = res.data || []
    },

    /** 获取乘客人群分析数据并渲染性别/年龄段/车票类型图表 */
    async fetchPassenger(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/passenger', params)
      this.passengerProfile = (res && res.code === 1 && res.data) ? res.data : {}
      this.$nextTick(() => {
        this.renderGenderChart()
        this.renderAgeChart()
        this.renderTicketChart()
      })
    },

    /** 获取线路×人群交叉分析数据 */
    async fetchLinePassenger(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/line-passenger', params)
      if (res && res.code === 1) this.linePassengerData = res.data || []
    },

    /** 获取司机经营排行TOP20数据 */
    async fetchDriver(params) {
      const p = { ...params, limit: 20 }
      const res = await this.$axios.get('/api/v1/manage/analysis/driver', p)
      if (res && res.code === 1) this.driverData = res.data || []
    },

    /** 获取车辆收入排行TOP20数据 */
    async fetchVehicle(params) {
      const p = { ...params, limit: 20 }
      const res = await this.$axios.get('/api/v1/manage/analysis/vehicle', p)
      if (res && res.code === 1) this.vehicleData = res.data || []
    },

    /** 获取新老用户订单趋势数据并渲染堆叠柱状+折线图 */
    async fetchUser(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/user', params)
      this.userData = (res && res.code === 1 && res.data) ? res.data : []
      this.$nextTick(() => this.renderUserTrendChart())
    },

    /** 获取时段分析数据并渲染按小时/按星期分布图 */
    async fetchTimeSlot(params) {
      const res = await this.$axios.get('/api/v1/manage/analysis/time-slot', params)
      this.timeSlotData = (res && res.code === 1 && res.data) ? res.data : []
      this.$nextTick(() => {
        this.renderHourChart()
        this.renderWeekChart()
      })
    },

    // ==================== 图表渲染 ====================

    /** 初始化/重置ECharts实例，避免内存泄漏 */
    initChart(refName) {
      if (this.charts[refName]) this.charts[refName].dispose()
      const dom = this.$refs[refName]
      if (!dom) return null
      const chart = echarts.init(dom)
      this.charts[refName] = chart
      return chart
    },

    /** 渲染国家/地区下单分布饼图：按国家维度聚合城市数据 */
    renderCountryChart() {
      const chart = this.initChart('countryChart')
      if (!chart) return
      const countryMap = {}
      this.cityData.forEach(item => {
        const country = item.country || '未知'
        countryMap[country] = (countryMap[country] || 0) + (item.orderCount || 0)
      })
      const data = Object.entries(countryMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
      const colors = ['#409EFF', '#F56C6C', '#67C23A', '#E6A23C', '#b37feb', '#909399', '#36cfc9', '#ff85c0']
      const hasData = data.some(d => d.value > 0)
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c}单 ({d}%)' },
        legend: { bottom: 5, type: 'scroll', textStyle: { fontSize: 11 } },
        color: colors,
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        series: [{
          type: 'pie',
          radius: ['35%', '58%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
          data: hasData ? data : []
        }]
      })
    },

    /** 渲染城市下单分布TOP10横向柱状图 */
    renderCityChart() {
      const chart = this.initChart('cityChart')
      if (!chart) return
      const top10 = this.cityData.slice(0, 10)
      const hasData = top10.some(i => (i.orderCount || 0) > 0)
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 10, right: 30, top: 10, bottom: 20, containLabel: true },
        xAxis: { type: 'value' },
        yAxis: {
          type: 'category',
          data: top10.map(i => i.city).reverse(),
          axisLabel: { fontSize: 11, width: 60, overflow: 'truncate' }
        },
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        series: [{
          type: 'bar',
          data: top10.map(i => i.orderCount).reverse(),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#409EFF' },
              { offset: 1, color: '#66B1FF' }
            ]),
            borderRadius: [0, 4, 4, 0]
          },
          label: { show: true, position: 'right', fontSize: 11 }
        }]
      })
    },

    /** 渲染乘客性别分布环形图 */
    renderGenderChart() {
      const chart = this.initChart('genderChart')
      if (!chart) return
      const p = this.passengerProfile
      const genderData = [
        { value: p.maleCount || 0, name: '男' },
        { value: p.femaleCount || 0, name: '女' },
        { value: p.unknownGenderCount || 0, name: '未知' },
      ]
      const hasData = genderData.some(d => d.value > 0)
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c}人 ({d}%)' },
        legend: { bottom: 5, textStyle: { fontSize: 11 } },
        color: ['#409EFF', '#F56C6C', '#909399'],
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        series: [{
          type: 'pie',
          radius: ['35%', '58%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          label: { show: false },
          emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
          data: hasData ? genderData : []
        }]
      })
    },

    /** 渲染乘客年龄段分布柱状图 */
    renderAgeChart() {
      const chart = this.initChart('ageChart')
      if (!chart) return
      const groups = (this.passengerProfile.ageGroups || [])
      const hasData = groups.some(g => (g.count || 0) > 0)
      const colors = ['#F56C6C', '#E6A23C', '#409EFF', '#67C23A', '#909399', '#b37feb', '#DCDFE6']
      chart.setOption({
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        grid: { left: 10, right: 10, top: 20, bottom: 10, containLabel: true },
        xAxis: { type: 'category', data: groups.map(g => g.ageGroup), axisLabel: { fontSize: 10 } },
        yAxis: { type: 'value' },
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        series: [{
          type: 'bar',
          data: groups.map((g, i) => ({
            value: g.count,
            itemStyle: { color: colors[i % colors.length], borderRadius: [4, 4, 0, 0] }
          })),
          label: { show: hasData, position: 'top', fontSize: 11 }
        }]
      })
    },

    /** 渲染车票类型分布饼图 */
    renderTicketChart() {
      const chart = this.initChart('ticketChart')
      if (!chart) return
      const types = (this.passengerProfile.ticketTypes || [])
      const hasData = types.some(t => (t.count || 0) > 0)
      const colors = ['#409EFF', '#E6A23C', '#67C23A', '#909399']
      chart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c}张 ({d}%)' },
        legend: { bottom: 5, textStyle: { fontSize: 11 } },
        color: colors,
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        series: [{
          type: 'pie',
          radius: ['35%', '58%'],
          center: ['50%', '45%'],
          label: { show: false },
          emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
          data: hasData ? types.map(t => ({ value: t.count, name: t.ticketType })) : []
        }]
      })
    },

    /** 渲染新老用户订单趋势堆叠柱状+折线组合图 */
    renderUserTrendChart() {
      const chart = this.initChart('userTrendChart')
      if (!chart) return
      const dates = this.userData.map(d => d.date)
      const hasData = this.userData.some(d => (d.totalOrders || 0) > 0)
      chart.setOption({
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        tooltip: { trigger: 'axis' },
        legend: { data: ['新客订单', '老客订单', '总订单'], bottom: 0, textStyle: { fontSize: 11 } },
        grid: { left: 10, right: 10, top: 20, bottom: 45, containLabel: true },
        xAxis: { type: 'category', data: dates, axisLabel: { rotate: dates.length > 10 ? 45 : 0, fontSize: 9 } },
        yAxis: { type: 'value' },
        series: [
          {
            name: '新客订单', type: 'bar', stack: 'total',
            data: this.userData.map(d => d.newCustomerOrders),
            itemStyle: { color: '#67C23A' }
          },
          {
            name: '老客订单', type: 'bar', stack: 'total',
            data: this.userData.map(d => d.oldCustomerOrders),
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '总订单', type: 'line', smooth: true,
            data: this.userData.map(d => d.totalOrders),
            lineStyle: { color: '#E6A23C', width: 2 },
            itemStyle: { color: '#E6A23C' }
          }
        ]
      })
    },

    /** 渲染下单时段分布柱状图（按小时0-23），高峰时段用红/橙色标注 */
    renderHourChart() {
      const chart = this.initChart('hourChart')
      if (!chart) return
      const hourData = this.timeSlotData.filter(d => d.hour !== null && d.hour !== undefined)
      hourData.sort((a, b) => a.hour - b.hour)
      const hasData = hourData.some(d => (d.orderCount || 0) > 0)
      chart.setOption({
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        tooltip: { trigger: 'axis' },
        grid: { left: 10, right: 10, top: 20, bottom: 10, containLabel: true },
        xAxis: { type: 'category', data: hourData.map(d => d.hour + ':00'), axisLabel: { fontSize: 9, rotate: 45 } },
        yAxis: { type: 'value' },
        series: [{
          type: 'bar',
          data: hourData.map(d => d.orderCount),
          itemStyle: {
            color: function(p) {
              const h = hourData[p.dataIndex].hour
              if (h >= 7 && h <= 9) return '#F56C6C'
              if (h >= 17 && h <= 19) return '#E6A23C'
              return '#409EFF'
            },
            borderRadius: [3, 3, 0, 0]
          }
        }]
      })
    },

    /** 渲染按星期下单分布柱状图，工作日蓝色/周末绿色区分 */
    renderWeekChart() {
      const chart = this.initChart('weekChart')
      if (!chart) return
      const weekData = this.timeSlotData.filter(d => d.dayOfWeek)
      const order = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
      weekData.sort((a, b) => order.indexOf(a.dayOfWeek) - order.indexOf(b.dayOfWeek))
      const colors = ['#409EFF', '#409EFF', '#409EFF', '#409EFF', '#409EFF', '#67C23A', '#67C23A']
      const hasData = weekData.some(d => (d.orderCount || 0) > 0)
      chart.setOption({
        graphic: hasData ? [] : [{ type: 'text', left: 'center', top: 'middle', style: { text: '暂无数据', fontSize: 13, fill: '#909399' } }],
        tooltip: { trigger: 'axis' },
        grid: { left: 10, right: 10, top: 20, bottom: 10, containLabel: true },
        xAxis: { type: 'category', data: weekData.map(d => d.dayOfWeek), axisLabel: { fontSize: 11 } },
        yAxis: { type: 'value' },
        series: [{
          type: 'bar',
          data: weekData.map((d, i) => ({
            value: d.orderCount,
            itemStyle: { color: colors[i], borderRadius: [4, 4, 0, 0] }
          })),
          label: { show: true, position: 'top', fontSize: 11 }
        }]
      })
    }
  }
}
</script>

<style lang="less" scoped>
.analysis-statistics {
  padding: 16px;
  background: #f5f7fa;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  .filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    padding: 16px 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    i {
      margin-right: 8px;
      color: #409EFF;
    }
  }

  .overview-cards {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-bottom: 24px;

    .ov-card {
      background: #fff;
      border-radius: 10px;
      padding: 16px;
      display: flex;
      align-items: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      }

      .ov-card-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 12px;
        flex-shrink: 0;
        i { color: #fff; font-size: 18px; }
      }

      .ov-card-info {
        min-width: 0;
        .ov-card-value {
          font-size: 20px;
          font-weight: 700;
          color: #303133;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ov-card-label {
          font-size: 12px;
          color: #909399;
          margin-top: 2px;
        }
      }
    }
  }

  .chart-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    min-width: 0;

    .chart-card-header {
      padding: 14px 20px;
      border-bottom: 1px solid #f0f0f0;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      i { margin-right: 6px; color: #409EFF; }
    }

    .chart-body {
      width: 100%;
      height: 320px;
      padding: 10px;
      box-sizing: border-box;
    }
  }

  .charts-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 20px;
  }

  .charts-grid-half {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }

  .table-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    padding: 16px;
  }

  .tables-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 24px;
  }

  // 表格整体样式
  .el-table {
    border-radius: 8px;
    overflow: hidden;
  }

  // 响应式 — 必须在scoped根选择器内部
  @media (max-width: 1400px) {
    .overview-cards {
      grid-template-columns: repeat(3, 1fr);
    }
    .charts-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 1000px) {
    .overview-cards {
      grid-template-columns: repeat(2, 1fr);
    }
    .charts-grid, .charts-grid-half {
      grid-template-columns: 1fr;
    }
    .tables-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
